<template>
  <div :class="[classes.header, 'header']">
    <OperatorLogo :class="classes.logo" />
    <div v-if="useTransition" :class="classes.transitionWrapper">
      <div :class="[classes.text, classes.fade, classes.fadeOut]">
        <DynamicText
          :class="classes.sport"
          tag="div"
          styleGroup="Fixture Sport"
          :get="fixtureTimeFont.get"
          :set="fixtureTimeFont.set"
        >
          <span>{{ eventSport }}</span>
        </DynamicText>
      </div>
      <StakeFixed v-if="showWinnings" :class="[classes.fade, classes.fadeIn]" />
      <CtaButton
        v-else
        :href="ctaButton.url"
        :text="ctaButton.text"
        :buttonId="ctaButton.id || ''"
        :class="[classes.ctaButton, classes.fade, classes.fadeIn]"
      />
    </div>
    <div v-else>
      <div v-if="eventSport.length > 0" :class="classes.text">
        <DynamicText
          :class="classes.sport"
          tag="div"
          styleGroup="Fixture Sport"
          :get="fixtureTimeFont.get"
          :set="fixtureTimeFont.set"
        >
          <span>{{ eventSport }}</span>
        </DynamicText>
      </div>
      <StakeFixed v-else-if="showWinnings" />
      <CtaButton
        v-else-if="displayCtaButton"
        :href="ctaButton.url"
        :text="ctaButton.text"
        :buttonId="ctaButton.id"
        :class="classes.ctaButton"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { Brand } from "@fresh8/types/src";
import { composeEvent } from "@fresh8/ad-data-composition/src/betData";
import { useShowWinnings } from "@fresh8/ad-data-composition/src/usePotentialWinnings";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import { getCtaButton } from "../hooks/useCtaButton";
import OperatorLogo from "./OperatorLogo.vue";
import DynamicText from "./DynamicText.vue";
import CtaButton from "./CtaButton.vue";
import StakeFixed from "./StakeFixed.vue";
import { getSport } from "../lib/language";

const useStyles = createUseStyles({
  header: {
    width: "51px",
    display: "flex",
    flexDirection: "column",
    alignContent: "center",
    paddingRight: "3px",

    "& .operator-logo": {
      margin: "auto"
    }
  },
  logo: {
    maxWidth: "51px",
    height: "16px",
    maxHeight: "16px",
    paddingBottom: "2px"
  },
  text: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    height: "16px",
    width: "51px",
    lineHeight: "normal",
    textAlign: "center"
  },
  sport: {
    display: "flex",
    justifyContent: "center",
    fontSize: "6px"
  },
  ctaButton: {
    padding: "2px 4px !important",
    height: "16px !important",
    width: "51px !important",

    "& .cta-text": {
      fontSize: "8px"
    }
  },
  transitionWrapper: {
    maxHeight: "16px"
  },
  fade: {
    animation: "$fadeInOut 3s linear 0.5s infinite",
    animationFillMode: "both",
    position: "absolute"
  },
  fadeIn: {
    animationDirection: "alternate"
  },
  fadeOut: {
    animationDirection: "alternate-reverse"
  },
  "@keyframes fadeInOut": {
    "0%": { opacity: 0 },
    "50%": { opacity: 0 },
    "60%": { opacity: 1 },
    "100%": { opacity: 1 }
  }
});
export default defineComponent({
  name: "Header",
  components: {
    OperatorLogo,
    DynamicText,
    StakeFixed,
    CtaButton
  },
  props: {
    eventId: { type: String, required: false, default: "" },
    syncCallback: {
      type: Function,
      required: false,
      default: undefined
    }
  },
  setup(props) {
    const brandContext = injectBrand();
    const event = composeEvent(props.eventId || "");
    const eventSport = computed(() =>
      !event.value ? "" : getSport(event.value.sport)
    );

    const showWinnings = useShowWinnings();

    const displayCtaButton = computed(
      () =>
        brandContext?.headerBranding.value?.type === "cta" &&
        !showWinnings.value
    );

    const useTransition = computed(
      () =>
        eventSport.value?.length > 0 &&
        (showWinnings.value || displayCtaButton.value)
    );

    const reactiveStyles = computed(() => ({
      displayCtaButton: displayCtaButton.value
    }));

    const ctaButton = getCtaButton();

    const classesRef = useStyles(reactiveStyles);

    return {
      classes: classesRef.value,
      useTransition,
      displayCtaButton,
      ctaButton,
      eventSport,
      showWinnings,
      fixtureTimeFont: {
        get(brand: Brand | undefined) {
          return brand && brand.adTemplate.market.fixtureTimeFont;
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureTimeFont = value;
        }
      }
    };
  },
  beforeUpdate() {
    if (this.syncCallback) {
      this.syncCallback();
    }
  }
});
</script>

<style></style>
